import { AnimatePresence, motion } from "framer-motion";
import { useLocation } from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import Close from "atoms/icon/icons/close.svg";
import HelpCircle from "atoms/icon/icons/help_circle.svg";
import { Analytics, AnalyticsEvents } from "modules/analytics";

import { normalizePersona } from "../adonis.helpers";
import { useAdonisChat } from "../AdonisChatProvider/AdonisChatProvider";
import { getPersonaImage } from "../assets/getPersonaImage";

/**
 * Persistent advisor launcher for tablet and desktop.
 *
 * On a phone the advisor is reached from the `TopNav` actions, which sit on
 * every screen. From `desk` up those actions live in the `DesktopHeader`
 * masthead, which scrolls away — so once a patient is a screen deep into a
 * long shop page the advisor is out of reach. This bubble keeps it one click
 * away, and the `?` badge says what it is for without carrying a label.
 *
 * It is hidden below `desk`, where the phone layout already carries the
 * `TopNav` entry point and a floating bubble would sit on top of the bottom
 * navigation bar.
 */
export const AdvisorLauncher = () => {
  const { t } = useTranslation();
  const { dismissLauncher, isEnabled, isLauncherDismissed, isOpen, openChat, persona } =
    useAdonisChat();
  const { pathname } = useLocation();

  const isExcludedRoute = ["/discover", "/rewards", "/wallet"].some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );

  if (!isEnabled || isExcludedRoute) return null;

  const normalizedPersona = normalizePersona(persona);

  const handleClick = () => {
    Analytics.track(AnalyticsEvents.ADONIS_VIEWED, {});
    openChat();
  };

  return (
    <AnimatePresence>
      {!isOpen && !isLauncherDismissed && (
        // The launcher sits in the window's bottom-right corner, the same corner
        // the docked chat panel opens into (`Modal`'s `desk:p-6`), so the chat
        // appears where the patient clicked. Page widths vary (full-width detail
        // pages, `max-w-6xl` shop lists), so pinning it to any one content column
        // puts it on top of content somewhere.
        //
        // Plain `fixed` is correct here even though this renders inside
        // `Root`'s `container-type: inline-size` element. Layout containment
        // normally makes an element the containing block for its fixed
        // descendants, but the CSSWG carved container queries out of that:
        // `container-type` alone does not, so this anchors to the viewport and
        // stays put while the page scrolls (verified in Chrome 151).
        //
        // z-40 keeps it above the `DesktopHeader` (z-30) and below the chat
        // panel and its dismiss layer (z-50).
        <motion.div
          className="fixed bottom-6 right-6 z-40 hidden desk:block"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          <motion.div
            data-testid="advisor-launcher"
            className="relative h-20 w-20 rounded-full bg-white shadow-[0_12px_28px_rgba(40,29,26,0.24)] ring-1 ring-black/5"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <button
              type="button"
              onClick={handleClick}
              aria-label={t("advisorLauncher.openChat", {
                personaName: normalizedPersona.name,
              })}
              className="h-full w-full overflow-hidden rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-color focus-visible:ring-offset-2"
            >
              <img
                src={getPersonaImage(normalizedPersona.name).logo}
                alt=""
                className="h-full w-full rounded-full object-cover"
              />
              <span className="absolute bottom-0 right-0 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-md ring-1 ring-black/5">
                <HelpCircle className="h-4 w-4 text-brand-color" />
              </span>
            </button>
            <button
              type="button"
              aria-label={t("advisorLauncher.dismiss")}
              className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-white text-secondary shadow-md ring-1 ring-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-color focus-visible:ring-offset-2"
              onClick={dismissLauncher}
            >
              <Close className="h-3 w-3" />
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
